import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  // Subscription data
  subscribed: boolean;
  subscriptionTier: string | null;
  subscriptionEnd: string | null;
  isPremium: boolean;
  isEnterprise: boolean;
  checkSubscription: () => Promise<void>;
  // Role-based access
  isAdmin: boolean;
  isModerator: boolean;
  userRoles: string[];
  rolesLoading: boolean;
  checkRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Subscription state
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  
  // Role state
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);

  const checkSubscription = async () => {
    if (!user || !session) {
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Subscription check error:', error);
        return;
      }

      setSubscribed(data.subscribed || false);
      setSubscriptionTier(data.subscription_tier || null);
      setSubscriptionEnd(data.subscription_end || null);
    } catch (error) {
      console.error('Subscription check failed:', error);
    }
  };

  const checkRoles = async () => {
    if (!user) {
      setUserRoles([]);
      setIsAdmin(false);
      setIsModerator(false);
      setRolesLoading(false);
      return;
    }

    setRolesLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        console.error('Role check error:', error);
        return;
      }

      const roles = (data?.map((r: any) => r.role) as string[]) || [];
      setUserRoles(roles);
      setIsAdmin(roles.includes('admin'));
      setIsModerator(roles.includes('moderator'));
    } catch (error) {
      console.error('Role check failed:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  // Check roles whenever user changes
  useEffect(() => {
    if (user) {
      checkRoles();
      checkSubscription();
    } else {
      setUserRoles([]);
      setIsAdmin(false);
      setIsModerator(false);
      setRolesLoading(false);
    }
  }, [user?.id]);

  // Auto-grant Enterprise subscription to admins
  useEffect(() => {
    const grantAdminSubscription = async () => {
      if (!user || !session || rolesLoading) return;
      
      // If user is admin but doesn't have Enterprise subscription
      if (isAdmin && subscriptionTier !== 'Enterprise') {
        try {
          const { error } = await supabase.functions.invoke('grant-admin-premium', {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });

          if (!error) {
            // Refresh subscription status
            await checkSubscription();
          }
        } catch (error) {
          // Silent fail - not critical
        }
      }
    };

    grantAdminSubscription();
  }, [isAdmin, subscriptionTier, rolesLoading, user?.id]);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Clear roles on sign out
        if (event === 'SIGNED_OUT') {
          setUserRoles([]);
          setIsAdmin(false);
          setIsModerator(false);
          setSubscribed(false);
          setSubscriptionTier(null);
          setSubscriptionEnd(null);
          setRolesLoading(false);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSubscribed(false);
    setSubscriptionTier(null);
    setSubscriptionEnd(null);
    setUserRoles([]);
    setIsAdmin(false);
    setIsModerator(false);
    setRolesLoading(false);
  };

  const isPremium = subscribed && (subscriptionTier === 'Premium' || subscriptionTier === 'Enterprise');
  const isEnterprise = subscribed && subscriptionTier === 'Enterprise';

  const value: AuthContextType = {
    user,
    session,
    loading,
    signOut,
    isAuthenticated: !!user,
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    isPremium,
    isEnterprise,
    checkSubscription,
    isAdmin,
    isModerator,
    userRoles,
    rolesLoading,
    checkRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};